import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardCheck, Check, X, MessageSquare, Eye, EyeOff, Filter, Play, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import MobileNav from '@/components/MobileNav';
import MentionInput from '@/components/MentionInput';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewPage = () => {
  const auth = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedPost, setSelectedPost] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedPost, setExpandedPost] = useState(null);
  const [internalComments, setInternalComments] = useState({});
  const [newInternalComment, setNewInternalComment] = useState('');
  const [loadingComments, setLoadingComments] = useState({});

  const fetchPosts = useCallback(async () => {
    try {
      let url = `${API}/review/posts`;
      if (filter === 'pending') {
        // Default - get submitted and needs_changes
      } else if (filter === 'all') {
        url += '?status=all';
      } else {
        url += `?status=${filter}`;
      }
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      toast.error('FAILED TO LOAD');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  }, []);

  const fetchInternalComments = async (postId) => {
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const response = await axios.get(`${API}/review/posts/${postId}/comments`);
      setInternalComments(prev => ({ ...prev, [postId]: response.data }));
    } catch (error) {
      console.error('Failed to load comments');
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleAddInternalComment = async (postId) => {
    if (!newInternalComment.trim()) return;
    
    try {
      const response = await axios.post(`${API}/review/posts/${postId}/comments`, {
        text: newInternalComment.trim()
      });
      setInternalComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), response.data]
      }));
      setNewInternalComment('');
    } catch (error) {
      toast.error('BŁĄD');
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchUnreadCount();
  }, [fetchPosts, fetchUnreadCount]);

  useEffect(() => {
    if (expandedPost) {
      fetchInternalComments(expandedPost);
    }
  }, [expandedPost]);

  const handleAction = async () => {
    if (!selectedPost || !actionType) return;

    if (actionType === 'approve' && !internalNotes.trim()) {
      toast.error('WYMAGANE SĄ NOTATKI WEWNĘTRZNE');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/review/posts/${selectedPost.id}`, {
        action: actionType,
        feedback: feedback || null,
        internal_notes: internalNotes || null,
        tagged_performers: []
      });
      
      toast.success(actionType === 'approve' ? 'ZATWIERDZONO' : actionType === 'reject' ? 'ODRZUCONO' : 'ZAKTUALIZOWANO');
      setSelectedPost(null);
      setActionType(null);
      setFeedback('');
      setInternalNotes('');
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'BŁĄD');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleVisibility = async (postId, currentStatus) => {
    try {
      const response = await axios.post(`${API}/review/posts/${postId}/toggle`);
      toast.success(response.data.message.toUpperCase());
      fetchPosts();
    } catch (error) {
      toast.error('BŁĄD');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUBMITTED': return 'bg-zom-yellow text-zom-black';
      case 'PUBLISHED': return 'bg-green-600 text-white';
      case 'NEEDS_CHANGES': return 'bg-orange-600 text-white';
      case 'REJECTED': return 'bg-zom-red text-white';
      case 'HIDDEN': return 'bg-gray-600 text-white';
      default: return 'bg-zom-white/20 text-white';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'SUBMITTED': return 'NOWY';
      case 'PUBLISHED': return 'PUB';
      case 'NEEDS_CHANGES': return 'ZMIANY';
      case 'REJECTED': return 'ODR';
      case 'HIDDEN': return 'UKRYTY';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zom-black flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-zom-yellow border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zom-black text-zom-white pb-24 pt-16">
      <MobileNav unreadCount={unreadCount} />
      
      {/* Header */}
      <header className="fixed top-10 left-0 right-0 z-30 bg-zom-black border-b-2 border-zom-yellow">
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-zom-yellow" strokeWidth={2.5} />
            <h1 className="text-lg font-heading text-zom-yellow tracking-wider">REVIEW</h1>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 bg-zom-black border-2 border-zom-yellow text-zom-white font-heading text-xs">
              <Filter className="w-4 h-4 mr-1" strokeWidth={2.5} />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zom-black border-2 border-zom-yellow">
              <SelectItem value="pending" className="font-heading text-xs">DO PRZEGLĄDU</SelectItem>
              <SelectItem value="PUBLISHED" className="font-heading text-xs">OPUBLIKOWANE</SelectItem>
              <SelectItem value="HIDDEN" className="font-heading text-xs">UKRYTE</SelectItem>
              <SelectItem value="REJECTED" className="font-heading text-xs">ODRZUCONE</SelectItem>
              <SelectItem value="all" className="font-heading text-xs">WSZYSTKIE</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Posts Grid - Thumbnails */}
      <div className="p-3 mt-12">
        {posts.length === 0 ? (
          <div className="text-center py-12 text-zom-white/50">
            <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" strokeWidth={2.5} />
            <p className="font-heading">BRAK POSTÓW</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {posts.map((post) => (
              <div 
                key={post.id}
                className={`relative aspect-square bg-zom-black border-2 cursor-pointer transition-all ${
                  expandedPost === post.id ? 'border-zom-yellow' : 'border-zom-white/30 hover:border-zom-white'
                }`}
                onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
              >
                {/* Thumbnail */}
                {post.media_type === 'video' ? (
                  <div className="relative w-full h-full">
                    <video 
                      src={post.media_url} 
                      className="w-full h-full object-cover"
                      muted
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play className="w-6 h-6 text-white" fill="white" />
                    </div>
                  </div>
                ) : (
                  <img 
                    src={post.media_url} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                )}
                
                {/* Status Badge */}
                <div className={`absolute top-1 right-1 px-1 py-0.5 text-[8px] font-heading ${getStatusColor(post.status)}`}>
                  {getStatusLabel(post.status)}
                </div>
                
                {/* Comments indicator */}
                {(internalComments[post.id]?.length > 0 || post.internal_comments_count > 0) && (
                  <div className="absolute bottom-1 left-1 px-1 py-0.5 bg-zom-blue text-white text-[8px] font-heading flex items-center gap-0.5">
                    <MessageSquare className="w-2 h-2" />
                    {internalComments[post.id]?.length || post.internal_comments_count || 0}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Expanded Post Details */}
      {expandedPost && (
        <div className="fixed bottom-20 left-0 right-0 bg-zom-black border-t-3 border-zom-yellow max-h-[60vh] overflow-y-auto z-40">
          {(() => {
            const post = posts.find(p => p.id === expandedPost);
            if (!post) return null;
            
            return (
              <div className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <span className="font-heading text-sm text-zom-yellow">
                    @{post.performer_email?.split('@')[0]?.toUpperCase()}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zom-white/50 font-body">
                      {formatDate(post.created_at)}
                    </span>
                    <button 
                      onClick={() => setExpandedPost(null)}
                      className="text-zom-white/50 hover:text-zom-white"
                    >
                      <ChevronDown className="w-5 h-5" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>

                {/* Caption */}
                {post.caption && (
                  <p className="text-sm text-zom-white/80 font-body mb-3">{post.caption}</p>
                )}

                {/* Actions */}
                <div className="flex gap-2 flex-wrap mb-4">
                  {post.status === 'SUBMITTED' || post.status === 'NEEDS_CHANGES' ? (
                    <>
                      <button
                        onClick={() => { setSelectedPost(post); setActionType('approve'); }}
                        className="flex-1 py-2 bg-green-600 text-white font-heading text-xs flex items-center justify-center gap-1"
                      >
                        <Check className="w-4 h-4" strokeWidth={2.5} />
                        ZATWIERDŹ
                      </button>
                      <button
                        onClick={() => { setSelectedPost(post); setActionType('reject'); }}
                        className="flex-1 py-2 bg-zom-red text-white font-heading text-xs flex items-center justify-center gap-1"
                      >
                        <X className="w-4 h-4" strokeWidth={2.5} />
                        ODRZUĆ
                      </button>
                      <button
                        onClick={() => { setSelectedPost(post); setActionType('request_changes'); }}
                        className="flex-1 py-2 bg-orange-600 text-white font-heading text-xs flex items-center justify-center gap-1"
                      >
                        <MessageSquare className="w-4 h-4" strokeWidth={2.5} />
                        ZMIANY
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleToggleVisibility(post.id, post.status)}
                      className={`flex-1 py-2 font-heading text-xs flex items-center justify-center gap-1 ${
                        post.status === 'PUBLISHED' 
                          ? 'bg-gray-600 text-white' 
                          : 'bg-green-600 text-white'
                      }`}
                    >
                      {post.status === 'PUBLISHED' ? (
                        <>
                          <EyeOff className="w-4 h-4" strokeWidth={2.5} />
                          UKRYJ
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" strokeWidth={2.5} />
                          POKAŻ
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Internal Comments Section */}
                <div className="border-t-2 border-zom-white/20 pt-3">
                  <h3 className="text-xs font-heading text-zom-blue mb-2 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" strokeWidth={2.5} />
                    KOMENTARZE WEWNĘTRZNE
                  </h3>
                  
                  {loadingComments[post.id] ? (
                    <div className="py-2 text-center">
                      <div className="w-4 h-4 border-2 border-zom-yellow border-t-transparent animate-spin mx-auto" />
                    </div>
                  ) : (
                    <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                      {(internalComments[post.id] || []).map(comment => (
                        <div key={comment.id} className="bg-zom-blue/20 p-2 border border-zom-blue/50">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-heading text-zom-blue">
                              @{comment.user_email?.split('@')[0]?.toUpperCase()}
                            </span>
                            <span className="text-[9px] text-zom-white/40">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-xs text-zom-white/90 font-body">{comment.text}</p>
                        </div>
                      ))}
                      {(internalComments[post.id] || []).length === 0 && (
                        <p className="text-[10px] text-zom-white/40 font-body">Brak komentarzy</p>
                      )}
                    </div>
                  )}
                  
                  {/* Add Comment */}
                  <div className="flex gap-2">
                    <input
                      value={newInternalComment}
                      onChange={(e) => setNewInternalComment(e.target.value)}
                      placeholder="Dodaj komentarz..."
                      className="flex-1 bg-zom-black border border-zom-blue text-zom-white px-2 py-1.5 text-xs font-body"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddInternalComment(post.id)}
                    />
                    <button
                      onClick={() => handleAddInternalComment(post.id)}
                      className="px-3 py-1.5 bg-zom-blue text-white"
                    >
                      <Send className="w-3 h-3" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Action Sheet */}
      <Sheet open={!!selectedPost && !!actionType} onOpenChange={() => { setSelectedPost(null); setActionType(null); }}>
        <SheetContent side="bottom" className="bg-zom-black border-t-3 border-zom-yellow text-zom-white max-h-[80vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading text-zom-yellow tracking-wider">
              {actionType === 'approve' ? 'ZATWIERDŹ POST' : 
               actionType === 'reject' ? 'ODRZUĆ POST' : 
               'POPROŚ O ZMIANY'}
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-4 mt-4">
            {/* Feedback for performer */}
            <div>
              <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">
                FEEDBACK DLA PERFORMERA
              </label>
              <MentionInput
                value={feedback}
                onChange={setFeedback}
                placeholder="Napisz feedback..."
                className="w-full bg-zom-black border-2 border-zom-white px-4 py-3 text-zom-white font-body min-h-[80px]"
              />
            </div>

            {/* Internal notes - required for approve */}
            <div>
              <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">
                NOTATKI WEWNĘTRZNE {actionType === 'approve' && '*'}
              </label>
              <textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Notatki widoczne tylko dla zespołu..."
                className="w-full bg-zom-black border-2 border-zom-white px-4 py-3 text-zom-white font-body min-h-[80px] resize-none"
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleAction}
              disabled={submitting}
              className={`w-full py-3 font-heading text-sm tracking-wider border-2 ${
                actionType === 'approve' ? 'bg-green-600 border-green-600 text-white' :
                actionType === 'reject' ? 'bg-zom-red border-zom-red text-white' :
                'bg-orange-600 border-orange-600 text-white'
              } disabled:opacity-50`}
            >
              {submitting ? 'PRZETWARZANIE...' : 
               actionType === 'approve' ? 'ZATWIERDŹ' :
               actionType === 'reject' ? 'ODRZUĆ' :
               'WYŚLIJ'}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ReviewPage;
